import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { getReportStatus } from '../api/PomReport';
import { getCompanies } from '../api/Company';
import { getSettings } from '../api/Settings';
import { ICompany, IPomReportDataInfo, REPORTSTATUS } from '../util/DataModelInterface'
import { DataTable } from './DataTable';
import { statusView } from './CommonControls';
import { DateSelector } from './DateSelector';
import { Spinner } from './Spinner';
import { actionButtons } from './ActionButtons';
import useToastHandler from '../hooks/useToastHandler';
import { Typeahead } from 'react-bootstrap-typeahead';
import { getCurrentPeriodString, formatFinCurrency, getDeadline } from '../util/FormatDateUtil';

interface IReportAdminStatusTableData {
  // cols: 'company', 'period', 'state', 'sum', 'modified'
  company: string | undefined,
  period: string,
  state: JSX.Element,
  sum: JSX.Element,
  modified: JSX.Element,
  actions: JSX.Element,
  id: string,
  status: string,
  companyId: string,
  periodTimestamp: string
}

export const ReportAdminStatus = () => {

  const intl = useIntl();
  const { addToast } = useToastHandler();

  const [reports, setReports] = useState([] as IPomReportDataInfo[]);
  const [companies, setCompanies] = useState([] as ICompany[]);
  const [period, setPeriod] = useState(getCurrentPeriodString());
  const [billingLimit, setBillingLimit] = useState(0);
  const [loading, setLoading] = useState(false);
  const [reportTableData, setReportTableData] = useState([] as IReportAdminStatusTableData[]);
  const [selections, setSelections] = useState([] as ICompany[]);

  useEffect(() => {
    getCompanies().then((data: any) => {
      if (data?.error) {
        addToast(data.error, 'companies', 'fetch');
      } else if (data) {
        setCompanies(data);
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getSettings().then((data: any) => {
      if (data?.error) {
        addToast(data.error, 'settings', 'fetch');
      } else if (data?.billing_limit)
        setBillingLimit(parseInt(data.billing_limit));
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (period) {
      setLoading(true);
      getReportStatus([], period).then((data: any) => {
        if (data?.error) {
          addToast(data.error, 'reports', 'fetch');
        } else if (data) {
          setReports(data);
        }
      }).finally(() => {
        setLoading(false);
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  useEffect(() => {
    parseTableData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selections, reports]);

  const comparer = (a: any, b: any) => {
    return a?.company?.localeCompare(b?.company);
  }

  const parseTableData = () => {
    let data = [] as IReportAdminStatusTableData[];
    // 'company', 'period', 'state', 'sum', 'modified'
    if(Array.isArray(reports)) {
      reports?.forEach((r) => {
        let name = r.name ? r.name : "";
        let compName = companies.find((c) => {return c.id === r.company})?.name;
        if (selections.length > 0 && !selections.find((c) => c.name === compName)) {
        } else {
          let date = new Date(r.period);
          let monthStr = period.split("-")[1];
          let yearStr = period.split("-")[0];
          let reportingPeriod = monthStr + "/" + yearStr;
          let mod = intl.formatDate(r.modified, { day: 'numeric', month: 'numeric', year: 'numeric' });
          const sum = (total_eur: number) => {
            let classes = ( (total_eur < billingLimit) &&
              (r.status === REPORTSTATUS.NEW || r.status === REPORTSTATUS.ONGOING || r.status === REPORTSTATUS.REOPENED)) ? "error" : "";
            let sumText = total_eur ? formatFinCurrency(total_eur) : "";
            return (<span className={classes}>{sumText}</span>);
          }
          const modView = (mod: string, name: string) => <>{mod}<br/>{name}</>;
          let periodDate = new Date(r.period);
          let timestamp = periodDate.getFullYear() + "-" + (periodDate.getMonth()+1);
          let item = {
            company: compName,
            period: reportingPeriod,
            state: statusView(r.status, getDeadline(date)),
            sum: sum(r.total_eur),
            modified: modView(mod, name),
            actions: actionButtons(r.company, compName ? compName : '', timestamp, r.status, "/reportsAdmin/reportAdmin", true),
            id: r.report_id,
            status: r.status,
            companyId: r.company,
            periodTimestamp: date.getFullYear() + "-" + (date.getMonth()+1)
          };
          data.push(item);
        }
      });
      data.sort(comparer);
      setReportTableData(data);
    }
  }

  const foundItem = (sel: ICompany[]) => {
    let selItem = sel[0];
    console.log("found", selItem);
    setSelections(sel);
  }

  const filterByFields = ['business_id', 'name'];

  return (
      
      <div id="tab-tabpane-status" role="tabpanel" aria-labelledby="tab-tab-status">
          <Spinner active={loading} />
          <DateSelector
              startPeriod={period}
              onChange={(start, ) => { setPeriod(start); }}
              showMonth={true}
              dateFormat='MM/y'
              inputFormat='yyyy-MM'/>
          <br/>
          <div className="search-group me-md-auto">
            <Typeahead
              id="typeahead-search-comp"
              inputProps={{
                'aria-label': intl.formatMessage({id: 'companies.search.text'}),
              }}
              clearButton
              filterBy={filterByFields}
              labelKey="name"
              onChange={(sel) => {foundItem(sel as ICompany[]);}}
              options={companies}
              placeholder={intl.formatMessage({id: 'companies.search.text'})}
              renderMenuItemChildren={(option) => (
                <div>
                  {(option as ICompany).name}
                  <div>
                    <small>{(option as ICompany).business_id}</small>
                  </div>
                </div>
              )}
            />
          </div>
          <div>
              { reportTableData.length > 0 &&
                <DataTable tableheadings={['company', 'period', 'state', 'sum', 'modified', 'actions']}
                    tabledata={reportTableData} caption='Company reports'
                    tableType='reports' tableId='statuslist'
                    optSortableColumns={['company', 'state']}
                />
              }
          </div>
      </div>
  );
};